import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { submitReport } from "@/lib/reports";
import { attributeStoredReferral } from "@/lib/referral";

interface UserProfile {
  age: number;
  country: string;
  gender: string;
  continent?: string;
}

interface User {
  id: string;
  username: string;
  email: string;
  profilePicture: string;
  coins: number;
  diamonds: number;
  isPro: boolean;
  proPlan: string | null;
  proExpiresAt: Date | null;
  isOnline: boolean;
  isAvailable: boolean;
  isAnonymous: boolean;
  bio?: string | null;
  auraScore?: number;
  profile?: UserProfile;
}

// Stable device id used for ban enforcement, coin-farming prevention, and
// guest persistence. Stored in BOTH localStorage and a long-lived cookie so
// that clearing one does not wipe the identity.
const DEVICE_KEY = "talklot_device_id";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365 * 5; // 5 years

function readCookie(name: string): string | null {
  try {
    const match = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&") + "=([^;]*)"));
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

function writeCookie(name: string, value: string) {
  try {
    const secure = location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax${secure}`;
  } catch {
    /* ignore */
  }
}

function getDeviceId(): string {
  let fromLs: string | null = null;
  let fromCookie: string | null = null;
  try { fromLs = localStorage.getItem(DEVICE_KEY); } catch { /* ignore */ }
  fromCookie = readCookie(DEVICE_KEY);

  // Prefer whichever exists; if both exist but differ, keep localStorage (most recent install path)
  let id = fromLs || fromCookie;
  if (!id) {
    id = (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`);
  }
  // Sync to BOTH stores so either one alone can recover the identity
  try { localStorage.setItem(DEVICE_KEY, id); } catch { /* ignore */ }
  if (fromCookie !== id) writeCookie(DEVICE_KEY, id);
  return id;
}

interface Message {
  id: string;
  text: string;
  content?: string;
  isSensitive?: boolean;
  failed?: boolean;
  sender: "me" | "them";
  timestamp: Date;
  editedAt?: Date | null;
  deletedAt?: Date | null;
}

interface Conversation {
  id: string;
  username: string;
  otherUserId: string;
  lastMessage: string;
  lastMessageTime: Date;
  unread: number;
  avatar?: string;
  isOnline: boolean;
  continent?: string;
  auraScore?: number;
  sensitiveContent?: boolean;
  /**
   * The OTHER participant's `last_read_at`. Used to render read receipts:
   * any of MY messages whose timestamp <= this value have been seen.
   */
  otherLastReadAt?: Date | null;
}

interface FriendRequest {
  id: string;
  username: string;
  fromUserId: string;
  isOnline: boolean;
}

interface Friend {
  id: string;
  username: string;
  friendUserId: string;
  isOnline: boolean;
  continent?: string;
  auraScore?: number;
  sensitiveContent?: boolean;
}

interface MatchFilters {
  genders: string[];
  country: string;
}

interface BanInfo {
  userId: string;
  reason: string | null;
  bannedUntil: string | null;
}

interface AppContextType {
  user: User | null;
  isAuthenticated: boolean;
  banInfo: BanInfo | null;
  hasSeenWelcome: boolean;
  hasOnboarded: boolean;
  hasSurveyed: boolean;
  hasAcceptedTerms: boolean;
  darkMode: boolean;
  toggleDarkMode: () => void;
  messages: Message[];
  conversations: Conversation[];
  activeConversation: string | null;
  conversationMessages: Record<string, Message[]>;
  blockedUsers: string[];
  reportedUsers: string[];
  matchFilters: MatchFilters;
  friendRequests: FriendRequest[];
  friends: Friend[];
  sentRequests: string[];
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, username: string) => Promise<void>;
  loginAsGuest: () => Promise<void>;
  upgradeAccount: (email: string, password: string, username?: string) => Promise<void>;
  logout: () => Promise<void>;
  completeWelcome: () => void;
  completeOnboarding: () => void;
  completeSurvey: (profile: UserProfile & { username: string }) => Promise<void>;
  updateUser: (updates: Partial<User>) => Promise<void>;
  toggleAvailability: () => Promise<void>;
  addMessage: (text: string, sender: "me" | "them") => void;
  clearMessages: () => void;
  spendCoins: (amount: number) => Promise<boolean>;
  spendDiamonds: (amount: number) => Promise<boolean>;
  setActiveConversation: (id: string | null) => void;
  sendConversationMessage: (conversationId: string, text: string) => Promise<string>;
  markConversationRead: (conversationId: string) => Promise<void>;
  blockUser: (username: string) => Promise<void>;
  unblockUser: (username: string) => Promise<void>;
  reportUser: (username: string, reason: string, context?: { type?: "chat" | "video" | "profile"; conversationId?: string | null; channel?: string | null }) => Promise<void>;
  setMatchFilters: (filters: MatchFilters) => void;
  sendFriendRequest: (username: string) => Promise<void>;
  acceptFriendRequest: (id: string) => Promise<void>;
  ignoreFriendRequest: (id: string) => Promise<void>;
  unfriend: (username: string) => Promise<void>;
  searchUsers: (query: string) => Promise<any[]>;
  getOrCreateConversation: (otherUserId: string) => Promise<string>;
  refreshBalance: () => Promise<void>;
  acceptTerms: () => Promise<void>;
  addAura: (amount: number, reason?: string) => Promise<void>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

function loadFromStorage<T>(key: string, fallback: T): T {
  try {
    const val = localStorage.getItem(key);
    return val ? JSON.parse(val) : fallback;
  } catch {
    return fallback;
  }
}

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const { toast } = useToast();
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [banInfo, setBanInfo] = useState<BanInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasSeenWelcome, setHasSeenWelcome] = useState(() => loadFromStorage("buzzchat_welcomed", false));
  const [hasOnboarded, setHasOnboarded] = useState(() => loadFromStorage("buzzchat_onboarded", false));
  const [hasSurveyed, setHasSurveyed] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversation, setActiveConversation] = useState<string | null>(null);
  const [conversationMessages, setConversationMessages] = useState<Record<string, Message[]>>({});
  const [blockedUsers, setBlockedUsers] = useState<string[]>([]);
  const [reportedUsers, setReportedUsers] = useState<string[]>([]);
  const [matchFilters, setMatchFilters] = useState<MatchFilters>({ genders: ["All"], country: "Global" });
  // NOTE: `country` field stores CONTINENT now (Global = any). Kept name for backward compatibility.
  const [friendRequests, setFriendRequests] = useState<FriendRequest[]>([]);
  const [friends, setFriends] = useState<Friend[]>([]);
  const [sentRequests, setSentRequests] = useState<string[]>([]);
  const [darkMode, setDarkMode] = useState(() => loadFromStorage("talklot_darkmode", true));

  // Apply theme class
  useEffect(() => {
    document.documentElement.classList.toggle("light", !darkMode);
    localStorage.setItem("talklot_darkmode", JSON.stringify(darkMode));
  }, [darkMode]);

  const toggleDarkMode = () => setDarkMode(prev => !prev);

  // Persist local flags
  useEffect(() => { localStorage.setItem("buzzchat_welcomed", JSON.stringify(hasSeenWelcome)); }, [hasSeenWelcome]);
  useEffect(() => { localStorage.setItem("buzzchat_onboarded", JSON.stringify(hasOnboarded)); }, [hasOnboarded]);

  // Load profile from Supabase
  const loadProfile = useCallback(async (userId: string) => {
    // Fetch profile + ban status in parallel — they're independent reads.
    const [profileRes, riskRes] = await Promise.all([
      supabase.from("profiles").select("*").eq("user_id", userId).single(),
      (supabase
        .from("user_risk_scores")
        .select("is_banned, banned_until, ban_reason")
        .eq("user_id", userId)
        .maybeSingle() as any),
    ]);

    const { data, error } = profileRes;
    if (error || !data) return null;
    const riskRow = (riskRes as any)?.data;

    // Ban enforcement: keep user signed in but lock them on the banned screen so they can submit a dispute.
    if (riskRow?.is_banned && (!riskRow.banned_until || new Date(riskRow.banned_until) > new Date())) {
      setBanInfo({
        userId,
        reason: riskRow.ban_reason ?? null,
        bannedUntil: riskRow.banned_until ?? null,
      });
      setIsAuthenticated(true);
      setLoading(false);
      return null;
    }
    setBanInfo(null);

    const profile: User = {
      id: userId,
      username: data.username,
      email: data.email,
      profilePicture: data.profile_picture || "",
      coins: data.coins,
      diamonds: (data as any).diamonds ?? 25,
      isPro: (data as any).is_pro ?? false,
      proPlan: (data as any).pro_plan ?? null,
      proExpiresAt: (data as any).pro_expires_at ? new Date((data as any).pro_expires_at) : null,
      isOnline: data.is_online,
      isAvailable: data.is_available,
      isAnonymous: (data as any).is_anonymous ?? false,
      bio: (data as any).bio ?? null,
      auraScore: (data as any).aura_score ?? 0,
      ...(data.survey_completed && data.age && data.country && data.gender ? {
        profile: { age: data.age, country: data.country, gender: data.gender, continent: (data as any).continent }
      } : {}),
    };
    
    setUser(profile);
    setHasSurveyed(data.survey_completed);
    setHasAcceptedTerms((data as any).terms_accepted ?? false);
    setIsAuthenticated(true);
    
    // Set online — fire-and-forget, don't block auth flow.
    void supabase.from("profiles").update({ is_online: true }).eq("user_id", userId);
    
    return profile;
  }, []);

  // Load friends
  const loadFriends = useCallback(async (userId: string) => {
    const { data } = await supabase
      .from("friendships")
      .select("id, friend_id")
      .eq("user_id", userId);
    
    if (!data || data.length === 0) { setFriends([]); return; }
    
    const friendIds = data.map(f => f.friend_id);
    const { data: profiles } = await (supabase
      .from("public_profiles" as any)
      .select("user_id, username, is_online, continent, aura_score, sensitive_content")
      .in("user_id", friendIds) as any);
    
    if (profiles) {
      setFriends(profiles.map((p: any) => ({
        id: data.find(d => d.friend_id === p.user_id)?.id || "",
        username: p.username,
        friendUserId: p.user_id,
        isOnline: p.is_online,
        continent: p.continent,
        auraScore: p.aura_score ?? 0,
        sensitiveContent: !!p.sensitive_content,
      })));
    }
  }, []);

  // Load friend requests
  const loadFriendRequests = useCallback(async (userId: string) => {
    const { data } = await supabase
      .from("friend_requests")
      .select("id, from_user_id, status")
      .eq("to_user_id", userId)
      .eq("status", "pending");
    
    if (!data || data.length === 0) { setFriendRequests([]); return; }
    
    const fromIds = data.map(r => r.from_user_id);
    const { data: profiles } = await (supabase
      .from("public_profiles" as any)
      .select("user_id, username, is_online")
      .in("user_id", fromIds) as any);
    
    if (profiles) {
      setFriendRequests(data.map(r => {
        const p = (profiles as any[]).find((p: any) => p.user_id === r.from_user_id);
        return {
          id: r.id,
          username: p?.username || "Unknown",
          fromUserId: r.from_user_id,
          isOnline: p?.is_online || false,
        };
      }));
    }

    // Load sent requests
    const { data: sent } = await supabase
      .from("friend_requests")
      .select("to_user_id")
      .eq("from_user_id", userId)
      .eq("status", "pending");
    
    if (sent) {
      const toIds = sent.map(s => s.to_user_id);
      const { data: sentProfiles } = await (supabase
        .from("public_profiles" as any)
        .select("user_id, username")
        .in("user_id", toIds) as any);
      setSentRequests((sentProfiles as any[])?.map((p: any) => p.username) || []);
    }
  }, []);

  // Load conversations
  const loadConversations = useCallback(async (userId: string) => {
    const { data: participants } = await supabase
      .from("conversation_participants")
      .select("conversation_id, last_read_at")
      .eq("user_id", userId);
    
    if (!participants || participants.length === 0) { setConversations([]); return; }
    
    const convIds = participants.map(p => p.conversation_id);
    const lastReadMap = new Map<string, string>();
    participants.forEach((p: any) => lastReadMap.set(p.conversation_id, p.last_read_at));

    // Exclude random/temporary conversations from the DM list. Random chat
    // sessions reuse the conversations table for live transport but must
    // never appear as persistent DMs.
    const { data: convRows } = await supabase
      .from("conversations")
      .select("id, is_random")
      .in("id", convIds);
    const dmConvIds = (convRows || [])
      .filter((c: any) => !c.is_random)
      .map((c: any) => c.id as string);
    if (dmConvIds.length === 0) { setConversations([]); return; }

    const { data: allParticipants } = await supabase
      .from("conversation_participants")
      .select("conversation_id, user_id, last_read_at")
      .in("conversation_id", dmConvIds)
      .neq("user_id", userId);
    
    if (!allParticipants || allParticipants.length === 0) { setConversations([]); return; }
    
    const otherUserIds = [...new Set(allParticipants.map(p => p.user_id))];
    const { data: profiles } = await (supabase
      .from("public_profiles" as any)
      .select("user_id, username, is_online, profile_picture, continent, aura_score, sensitive_content")
      .in("user_id", otherUserIds) as any);
    
    // Batch fetch last messages for all conversations at once
    const lastMsgPromises = dmConvIds.map(convId =>
      supabase
        .from("messages")
        .select("text, content, created_at, conversation_id")
        .eq("conversation_id", convId)
        .order("created_at", { ascending: false })
        .limit(1)
        .single()
    );

    // Batch fetch unread counts (messages from others after last_read_at)
    const unreadPromises = dmConvIds.map(async (convId) => {
      const lastRead = lastReadMap.get(convId) || new Date(0).toISOString();
      const { count } = await supabase
        .from("messages")
        .select("id", { count: "exact", head: true })
        .eq("conversation_id", convId)
        .neq("sender_id", userId)
        .gt("created_at", lastRead);
      return { convId, count: count || 0 };
    });

    const [lastMsgResults, unreadResults] = await Promise.all([
      Promise.all(lastMsgPromises),
      Promise.all(unreadPromises),
    ]);

    const lastMsgMap = new Map<string, { text: string; content?: string | null; created_at: string }>();
    lastMsgResults.forEach(r => {
      if (r.data) lastMsgMap.set(r.data.conversation_id, r.data);
    });
    const unreadMap = new Map<string, number>();
    unreadResults.forEach(r => unreadMap.set(r.convId, r.count));

    const convList: Conversation[] = [];
    for (const convId of dmConvIds) {
      const other = allParticipants.find(p => p.conversation_id === convId) as any;
      if (!other) continue;
      const profile = (profiles as any[])?.find((p: any) => p.user_id === other.user_id);
      if (!profile) continue;
      
      const lastMsg = lastMsgMap.get(convId);
      convList.push({
        id: convId,
        username: profile.username,
        otherUserId: other.user_id,
        lastMessage: lastMsg?.content || lastMsg?.text || "No messages yet",
        lastMessageTime: new Date(lastMsg?.created_at || Date.now()),
        unread: unreadMap.get(convId) || 0,
        avatar: profile.profile_picture || undefined,
        isOnline: profile.is_online,
        continent: profile.continent,
        auraScore: profile.aura_score ?? 0,
        sensitiveContent: !!profile.sensitive_content,
        otherLastReadAt: other.last_read_at ? new Date(other.last_read_at) : null,
      });
    }
    
    convList.sort((a, b) => b.lastMessageTime.getTime() - a.lastMessageTime.getTime());
    setConversations(convList);
  }, []);

  // Load messages for active conversation
  const loadConversationMessages = useCallback(async (conversationId: string, userId: string) => {
    const { data } = await supabase
      .from("messages")
      .select("id, sender_id, text, content, is_sensitive, created_at, edited_at, deleted_at")
      .eq("conversation_id", conversationId)
      .order("created_at", { ascending: true });
    
    if (data) {
      setConversationMessages(prev => ({
        ...prev,
        [conversationId]: data.map(m => ({
          id: m.id,
          text: (m as any).content || m.text,
          content: (m as any).content || m.text,
          isSensitive: Boolean((m as any).is_sensitive),
          sender: m.sender_id === userId ? "me" as const : "them" as const,
          timestamp: new Date(m.created_at),
          editedAt: (m as any).edited_at ? new Date((m as any).edited_at) : null,
          deletedAt: (m as any).deleted_at ? new Date((m as any).deleted_at) : null,
        })),
      }));
    }
  }, []);

  // Auth state listener — restore session first, then listen for changes
  const loadingRef = React.useRef<string | null>(null);
  const initializedRef = React.useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const loadUserData = async (userId: string) => {
      if (loadingRef.current === userId) return;
      loadingRef.current = userId;
      // Profile is the only thing the home screen actually needs to render —
      // unlock the UI as soon as it resolves. Secondary lists hydrate in the
      // background so login/guest entry feels near-instant.
      await loadProfile(userId);
      setLoading(false);
      void Promise.all([
        loadFriends(userId),
        loadFriendRequests(userId),
        loadConversations(userId),
      ]).catch(() => {});
    };

    // 1. Restore session from storage FIRST
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        loadUserData(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // 2. Listen for subsequent auth changes (login, logout, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'TOKEN_REFRESHED') return; // Don't reset state on token refresh
      if (event === 'SIGNED_OUT') {
        loadingRef.current = null;
        setUser(null);
        setIsAuthenticated(false);
        setHasSurveyed(false);
        setHasAcceptedTerms(false);
        setFriends([]);
        setFriendRequests([]);
        setConversations([]);
        setConversationMessages({});
        setLoading(false);
        return;
      }
      if (session?.user && event === 'SIGNED_IN') {
        loadUserData(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, [loadProfile, loadFriends, loadFriendRequests, loadConversations]);

  // Stable ref for currently-open conversation (so realtime listener can avoid bumping unread)
  const activeConversationRef = React.useRef<string | null>(null);
  useEffect(() => { activeConversationRef.current = activeConversation; }, [activeConversation]);

  // Stable ref for the current DM list so the realtime listener can ignore
  // inserts for non-DM (random-chat) conversations without re-subscribing.
  const conversationsRef = React.useRef<Conversation[]>([]);
  useEffect(() => { conversationsRef.current = conversations; }, [conversations]);

  // Realtime: messages
  useEffect(() => {
    if (!user) return;
    
    const channel = supabase
      .channel("realtime-messages")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "messages" }, (payload) => {
        const msg = payload.new as any;
        const isMine = msg.sender_id === user.id;
        const isActive = activeConversationRef.current === msg.conversation_id;

        // Strict separation: ignore inserts for conversations that aren't
        // already in our DM list. Random-chat sessions are handled locally
        // by RandomChatPage / CallPage and must not pollute DM state.
        const knownDm = conversationsRef.current.some(c => c.id === msg.conversation_id);
        if (!knownDm && !isActive) {
          // New conversation we haven't seen yet (e.g. a freshly-added friend
          // sent the first message). Refresh the DM list so the chat appears
          // in the Messages tab with an unread badge — but skip random chats.
          (async () => {
            const { data: conv } = await supabase
              .from("conversations").select("is_random").eq("id", msg.conversation_id).maybeSingle();
            if (conv && !(conv as any).is_random && userIdRef.current) {
              await loadConversations(userIdRef.current);
            }
          })();
          return;
        }

        setConversationMessages(prev => {
          const existing = prev[msg.conversation_id] || [];
          if (existing.some(m => m.id === msg.id)) return prev;
          return {
            ...prev,
            [msg.conversation_id]: [...existing, {
              id: msg.id,
              text: msg.content || msg.text,
              content: msg.content || msg.text,
              isSensitive: Boolean(msg.is_sensitive),
              sender: isMine ? "me" : "them",
              timestamp: new Date(msg.created_at),
            }],
          };
        });
        setConversations(prev => prev.map(c => 
          c.id === msg.conversation_id 
            ? {
                ...c,
                lastMessage: msg.content || msg.text,
                lastMessageTime: new Date(msg.created_at),
                // bump unread only for incoming messages on a conversation that isn't open
                unread: !isMine && !isActive ? c.unread + 1 : c.unread,
              }
            : c
        ));

        // If this message arrives in the conversation we're already viewing, immediately mark as read
        if (!isMine && isActive) {
          void supabase.rpc("mark_conversation_read", { p_conversation_id: msg.conversation_id });
        }
      })
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "messages" }, (payload) => {
        const msg = payload.new as any;
        setConversationMessages(prev => ({
          ...prev,
          [msg.conversation_id]: (prev[msg.conversation_id] || []).map(m =>
            m.id === msg.id ? {
              ...m,
              text: msg.content || msg.text,
              content: msg.content || msg.text,
              isSensitive: Boolean(msg.is_sensitive),
              editedAt: msg.edited_at ? new Date(msg.edited_at) : null,
              deletedAt: msg.deleted_at ? new Date(msg.deleted_at) : null,
            } : m
          ),
        }));
      })
      .subscribe();
    
    return () => { supabase.removeChannel(channel); };
  }, [user]);

  // Stable ref for user id so realtime listener doesn't re-subscribe on every state change
  const userIdRef = React.useRef<string | null>(null);
  useEffect(() => { userIdRef.current = user?.id ?? null; }, [user?.id]);

  // Realtime: profiles (online status + own balance sync)
  useEffect(() => {
    if (!user?.id) return;
    const currentUserId = user.id;
    
    const channel = supabase
      .channel("realtime-profiles")
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "profiles" }, (payload) => {
        const updated = payload.new as any;
        // Sync own balance in real-time
        if (updated.user_id === currentUserId) {
          setUser(prev => prev ? {
            ...prev,
            coins: updated.coins ?? prev.coins,
            diamonds: updated.diamonds ?? prev.diamonds,
            isPro: updated.is_pro ?? prev.isPro,
            proPlan: updated.pro_plan ?? prev.proPlan,
            proExpiresAt: updated.pro_expires_at ? new Date(updated.pro_expires_at) : prev.proExpiresAt,
          } : null);
        }
        setFriends(prev => prev.map(f => 
          f.friendUserId === updated.user_id ? { ...f, isOnline: updated.is_online } : f
        ));
        setConversations(prev => prev.map(c => 
          c.otherUserId === updated.user_id ? { ...c, isOnline: updated.is_online } : c
        ));
      })
      .subscribe();
    
    return () => { supabase.removeChannel(channel); };
  // Only re-subscribe when user.id changes, not on every balance update
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Realtime: read receipts.
  // The other participant's `last_read_at` advances whenever they open the
  // conversation. We use this to render single/double checkmarks on my
  // outgoing messages without a dedicated read_at column on `messages`.
  useEffect(() => {
    if (!user?.id) return;
    const currentUserId = user.id;
    const channel = supabase
      .channel("realtime-read-receipts")
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "conversation_participants" },
        (payload) => {
          const row = payload.new as any;
          if (!row || row.user_id === currentUserId) return; // only track the OTHER participant
          const lastReadAt = row.last_read_at ? new Date(row.last_read_at) : null;
          setConversations((prev) =>
            prev.map((c) =>
              c.id === row.conversation_id ? { ...c, otherLastReadAt: lastReadAt } : c
            )
          );
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id]);

  // Realtime: friend requests
  useEffect(() => {
    if (!user) return;
    
    const channel = supabase
      .channel("realtime-friend-requests")
      .on("postgres_changes", { event: "*", schema: "public", table: "friend_requests" }, () => {
        loadFriendRequests(user.id);
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "friendships" }, () => {
        loadFriends(user.id);
      })
      .subscribe();
    
    return () => { supabase.removeChannel(channel); };
  }, [user, loadFriendRequests, loadFriends]);

  // Load messages when active conversation changes
  useEffect(() => {
    if (activeConversation && user) {
      loadConversationMessages(activeConversation, user.id);
    }
    const syncOpenConversation = () => {
      if (!document.hidden && activeConversation && user) {
        void loadConversationMessages(activeConversation, user.id);
      }
    };
    window.addEventListener("focus", syncOpenConversation);
    document.addEventListener("visibilitychange", syncOpenConversation);
    return () => {
      window.removeEventListener("focus", syncOpenConversation);
      document.removeEventListener("visibilitychange", syncOpenConversation);
    };
  }, [activeConversation, user, loadConversationMessages]);

  // Real presence: heartbeat + tab close = offline
  useEffect(() => {
    if (!user?.id) return;
    const userId = user.id;
    let mounted = true;

    const setOnline = async (online: boolean) => {
      if (!mounted) return;
      await supabase.from("profiles").update({
        is_online: online,
        ...(online ? { last_seen_at: new Date().toISOString() } : { last_seen_at: new Date().toISOString() }),
      } as any).eq("user_id", userId);
    };

    // Mark online immediately
    void setOnline(true);

    // Heartbeat every 30s to keep presence fresh
    const heartbeat = setInterval(() => { void setOnline(true); }, 30000);

    // Sync on tab visibility
    const handleVisibility = () => {
      void setOnline(!document.hidden);
    };
    document.addEventListener("visibilitychange", handleVisibility);

    // Sync on tab close
    const handleUnload = () => {
      // Use sendBeacon-style fire-and-forget; Supabase update may not flush, so also set sync flag
      void supabase.from("profiles").update({ is_online: false, last_seen_at: new Date().toISOString() } as any).eq("user_id", userId);
    };
    window.addEventListener("beforeunload", handleUnload);
    window.addEventListener("pagehide", handleUnload);

    return () => {
      mounted = false;
      clearInterval(heartbeat);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("beforeunload", handleUnload);
      window.removeEventListener("pagehide", handleUnload);
      void setOnline(false);
    };
  }, [user?.id]);

  const login = async (email: string, password: string) => {
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setLoading(false);
      toast({ title: "Login failed", description: error.message, variant: "destructive" });
      throw error;
    }
  };

  // Throws a tagged error if a profile already exists on this device. Caller
  // can catch `(e as any).code === "device_has_account"` to redirect to login.
  const ensureNoExistingDeviceAccount = async () => {
    const deviceId = getDeviceId();
    try {
      const { data } = await (supabase.rpc as any)("check_device_account_exists", { p_device_id: deviceId });
      if (data && data.exists) {
        const err: any = new Error(
          data.is_anonymous
            ? "You already have a guest account on this device. Please log in or continue as guest."
            : "You already have an account on this device. Please log in to continue."
        );
        err.code = "device_has_account";
        err.isAnonymous = !!data.is_anonymous;
        err.username = data.username || null;
        throw err;
      }
    } catch (e: any) {
      if (e?.code === "device_has_account") throw e;
      // Network / RPC failure → fail open (don't block real users)
      console.warn("Device account check failed (non-fatal):", e);
    }
  };

  const signup = async (email: string, password: string, username: string) => {
    setLoading(true);
    try {
      const normalizedEmail = email.trim().toLowerCase();
      const normalizedUsername = username.trim().toLowerCase();

      // Block duplicate accounts on the same device
      await ensureNoExistingDeviceAccount();

      // Pre-check uniqueness (case-insensitive). Two separate queries avoid
      // PostgREST `.or()` injection issues with special characters in email.
      const [emailRes, usernameRes] = await Promise.all([
        (supabase.from("profiles").select("user_id").eq("email", normalizedEmail).limit(1) as any),
        (supabase.from("profiles").select("user_id").eq("username", normalizedUsername).limit(1) as any),
      ]);

      if (emailRes?.data && emailRes.data.length > 0) {
        const msg = "This email is already linked to an existing account. Please log in instead.";
        toast({ title: "Email already registered", description: msg, variant: "destructive" });
        throw new Error(msg);
      }
      if (usernameRes?.data && usernameRes.data.length > 0) {
        const msg = "This username is already taken. Please choose another one.";
        toast({ title: "Username taken", description: msg, variant: "destructive" });
        throw new Error(msg);
      }

      const { data, error } = await supabase.auth.signUp({
        email: normalizedEmail,
        password,
        options: { data: { username: normalizedUsername } },
      });

      if (error) {
        const m = error.message?.toLowerCase() || "";
        let title = "Signup failed";
        let friendly = error.message;
        if (m.includes("already registered") || m.includes("already exists") || m.includes("user already") || (m.includes("email") && m.includes("exist"))) {
          title = "Email already registered";
          friendly = "This email is already linked to an existing account. Please log in instead.";
        } else if (m.includes("username") || m.includes("duplicate") || m.includes("23505")) {
          title = "Username taken";
          friendly = "This username is already taken. Please choose another one.";
        }
        toast({ title, description: friendly, variant: "destructive" });
        throw error;
      }

      // Bind device id to the new account so future signup attempts on this
      // device hit the device-account-exists guard.
      try {
        const deviceId = getDeviceId();
        await (supabase.rpc as any)("bind_device_id", { p_device_id: deviceId });
      } catch (e) {
        console.warn("bind_device_id failed (non-fatal):", e);
      }

      // Attribute promoter referral if a code was stored
      try { await attributeStoredReferral(); } catch {}

      // Welcome email — fire and forget, never block signup.
      try {
        if (data.user?.id) {
          void supabase.functions.invoke("send-email", {
            body: {
              type: "welcome",
              to: normalizedEmail,
              user_id: data.user.id,
              idempotency_key: `welcome-${data.user.id}`,
              data: { username: normalizedUsername },
            },
          });
        }
      } catch {}

      if (data.session) {
        toast({
          title: "Welcome to TalkLot! 🎉",
          description: "Your account is ready.",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const loginAsGuest = async () => {
    setLoading(true);
    try {
      const deviceId = getDeviceId();

      // 1. Device-level ban check — block guest creation entirely if device is banned
      try {
        const { data: banned } = await (supabase.rpc as any)("is_device_banned", { _device_id: deviceId });
        if (banned === true) {
          setLoading(false);
          toast({
            title: "Device suspended",
            description: "This device is currently suspended. You cannot continue as a guest.",
            variant: "destructive",
          });
          throw new Error("device_banned");
        }
      } catch (e: any) {
        if (e?.message === "device_banned") throw e;
        // Network failure on the check → fall through (don't lock real users out on a hiccup)
        console.warn("Device ban check failed (non-fatal):", e);
      }

      // 2. If a guest already exists on this device, do NOT create a new one.
      //    Supabase anon sessions auto-persist via localStorage; if their session
      //    is gone (cleared storage) we cannot silently re-auth without a password,
      //    so guide the user to use the existing account instead of farming a new one.
      try {
        await ensureNoExistingDeviceAccount();
      } catch (e: any) {
        setLoading(false);
        if (e?.code === "device_has_account") {
          toast({
            title: e.isAnonymous ? "Welcome back" : "Account already on this device",
            description: e.isAnonymous
              ? "We found your existing guest session on this device. Please use the same browser to continue, or create a full account to access it from anywhere."
              : e.message,
            variant: "destructive",
          });
        }
        throw e;
      }

      const { data, error } = await supabase.auth.signInAnonymously();
      if (error || !data.user) {
        toast({ title: "Couldn't start guest session", description: error?.message ?? "Try again", variant: "destructive" });
        setLoading(false);
        throw error ?? new Error("guest signup failed");
      }
      // Bind device id immediately, then claim one-time bonus (both idempotent)
      try {
        await (supabase.rpc as any)("bind_device_id", { p_device_id: deviceId });
        await (supabase.rpc as any)("claim_guest_signup_bonus", { p_device_id: deviceId });
      } catch (e) {
        console.warn("Guest bonus claim failed (non-fatal):", e);
      }
      try { await attributeStoredReferral(); } catch {}
      // Mark welcome/onboarding/survey/terms auto-complete for guests so they go straight in
      setHasSeenWelcome(true);
      setHasOnboarded(true);
    } catch (e) {
      throw e;
    }
  };

  const upgradeAccount = async (email: string, password: string, username?: string) => {
    if (!user) throw new Error("Not signed in");
    setLoading(true);
    try {
      const normalizedEmail = email.trim().toLowerCase();
      const normalizedUsername = username?.trim().toLowerCase() || "";

      // Pre-check uniqueness — exclude current (guest) user from the lookup
      const checks: Promise<any>[] = [
        (supabase.from("profiles").select("user_id").eq("email", normalizedEmail).neq("user_id", user.id).limit(1) as any),
      ];
      if (normalizedUsername) {
        checks.push(
          (supabase.from("profiles").select("user_id").eq("username", normalizedUsername).neq("user_id", user.id).limit(1) as any)
        );
      }
      const results = await Promise.all(checks);
      if (results[0]?.data && results[0].data.length > 0) {
        const msg = "This email is already linked to an existing account. Please log in instead.";
        toast({ title: "Email already registered", description: msg, variant: "destructive" });
        throw new Error(msg);
      }
      if (results[1]?.data && results[1].data.length > 0) {
        const msg = "This username is already taken. Please choose another one.";
        toast({ title: "Username taken", description: msg, variant: "destructive" });
        throw new Error(msg);
      }

      // Link email + password to the existing anonymous auth.user (preserves user_id)
      const { error: updateErr } = await supabase.auth.updateUser({ email: normalizedEmail, password });
      if (updateErr) {
        const m = updateErr.message?.toLowerCase() || "";
        let title = "Upgrade failed";
        let friendly = updateErr.message;
        if (m.includes("already") || m.includes("exist") || m.includes("registered")) {
          title = "Email already registered";
          friendly = "This email is already linked to an existing account. Please log in instead.";
        }
        toast({ title, description: friendly, variant: "destructive" });
        throw updateErr;
      }
      const { error: convertErr } = await (supabase.rpc as any)("convert_guest_to_registered", {
        p_username: username?.trim() || null,
      });
      if (convertErr) {
        const m = convertErr.message?.toLowerCase() || "";
        let title = "Upgrade failed";
        let friendly = convertErr.message;
        if (m.includes("23505") || m.includes("duplicate") || m.includes("unique")) {
          if (m.includes("username")) {
            title = "Username taken";
            friendly = "This username is already taken. Please choose another one.";
          } else if (m.includes("email")) {
            title = "Email already registered";
            friendly = "This email is already linked to an existing account. Please log in instead.";
          }
        }
        toast({ title, description: friendly, variant: "destructive" });
        throw convertErr;
      }
      try {
        const { data: bonus } = await (supabase.rpc as any)("claim_upgrade_bonus");
        if (bonus?.success) {
          toast({ title: "Welcome aboard! 🎉", description: `+${bonus.coins} coins, +${bonus.diamonds} diamonds` });
        }
      } catch {}
      try { await attributeStoredReferral(); } catch {}
      await loadProfile(user.id);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    if (user) {
      await supabase.from("profiles").update({ is_online: false }).eq("user_id", user.id);
    }
    await supabase.auth.signOut();
  };

  const completeWelcome = () => setHasSeenWelcome(true);
  const completeOnboarding = () => setHasOnboarded(true);

  const completeSurvey = async (data: UserProfile & { username: string }) => {
    if (!user) return;
    // `country` field stores the chosen CONTINENT (signup is continent-only now).
    // The DB trigger `sync_profile_continent` will mirror it into the `continent` column.
    const { error } = await supabase.from("profiles").update({
      username: data.username,
      age: data.age,
      country: data.country,
      gender: data.gender,
      survey_completed: true,
    }).eq("user_id", user.id);

    if (error) {
      toast({ title: "Error", description: "Failed to save profile", variant: "destructive" });
      return;
    }

    setUser(prev => prev ? { ...prev, username: data.username, profile: { age: data.age, country: data.country, gender: data.gender, continent: data.country } } : null);
    setHasSurveyed(true);
  };

  const updateUser = async (updates: Partial<User>) => {
    if (!user) return;
    // Guests cannot change their username — locked to Anonymous#### until upgrade
    if (user.isAnonymous && updates.username !== undefined && updates.username !== user.username) {
      const err: any = new Error("GUEST_BLOCKED");
      err.code = "GUEST_BLOCKED";
      throw err;
    }
    const dbUpdates: any = {};
    if (updates.username !== undefined) dbUpdates.username = updates.username;
    if (updates.profilePicture !== undefined) dbUpdates.profile_picture = updates.profilePicture;
    if (updates.isAvailable !== undefined) dbUpdates.is_available = updates.isAvailable;
    if ((updates as any).bio !== undefined) dbUpdates.bio = (updates as any).bio;
    
    if (Object.keys(dbUpdates).length > 0) {
      const { error } = await supabase.from("profiles").update(dbUpdates).eq("user_id", user.id);
      if (error) {
        toast({ title: "Error", description: "Failed to update profile", variant: "destructive" });
        return;
      }
    }
    setUser(prev => prev ? { ...prev, ...updates } : null);
  };

  const toggleAvailability = async () => {
    if (!user) return;
    const newVal = !user.isAvailable;
    await supabase.from("profiles").update({ is_available: newVal, is_online: newVal }).eq("user_id", user.id);
    setUser(prev => prev ? { ...prev, isAvailable: newVal, isOnline: newVal } : null);
  };

  const addMessage = (text: string, sender: "me" | "them") => {
    setMessages(prev => [...prev, { id: Date.now().toString(), text, sender, timestamp: new Date() }]);
  };

  const clearMessages = () => setMessages([]);

  const spendCoins = async (amount: number): Promise<boolean> => {
    if (!user || user.coins < amount) {
      toast({ title: "Not enough coins", description: `You need ${amount} coins. Visit the Shop to get more!`, variant: "destructive" });
      return false;
    }
    const { data, error } = await supabase.rpc("spend_coins" as any, { p_amount: amount });
    if (error || data === false) return false;
    setUser(prev => prev ? { ...prev, coins: prev.coins - amount } : null);
    return true;
  };

  const spendDiamonds = async (amount: number): Promise<boolean> => {
    if (!user || user.diamonds < amount) {
      toast({ title: "Not enough diamonds", description: `You need ${amount} diamond(s). Visit the Shop to get more!`, variant: "destructive" });
      return false;
    }
    const { data, error } = await supabase.rpc("spend_diamonds" as any, { p_amount: amount });
    if (error || data === false) return false;
    setUser(prev => prev ? { ...prev, diamonds: prev.diamonds - amount } : null);
    return true;
  };

  const blockUser = async (username: string) => {
    if (!user) return;
    const { data: target } = await (supabase.from("public_profiles" as any) as any).select("user_id").eq("username", username).single();
    if (!target) return;

    // 1. Insert block (idempotent)
    await supabase.from("blocked_users").insert({ blocker_id: user.id, blocked_id: target.user_id });

    // 2. Remove friendship both ways
    await supabase.from("friendships").delete().eq("user_id", user.id).eq("friend_id", target.user_id);
    await supabase.from("friendships").delete().eq("user_id", target.user_id).eq("friend_id", user.id);

    // 3. Cancel any pending friend requests
    await supabase.from("friend_requests").delete()
      .or(`and(from_user_id.eq.${user.id},to_user_id.eq.${target.user_id}),and(from_user_id.eq.${target.user_id},to_user_id.eq.${user.id})`);

    // 4. Update local state. Keep DM history permanent; message sending is blocked by the block trigger.
    setBlockedUsers(prev => prev.includes(username) ? prev : [...prev, username]);
    setFriends(prev => prev.filter(f => f.friendUserId !== target.user_id));
    setFriendRequests(prev => prev.filter(r => r.fromUserId !== target.user_id));
  };

  const unblockUser = async (username: string) => {
    if (!user) return;
    const { data: target } = await (supabase.from("public_profiles" as any) as any).select("user_id").eq("username", username).single();
    if (target) {
      await supabase.from("blocked_users").delete().eq("blocker_id", user.id).eq("blocked_id", target.user_id);
      setBlockedUsers(prev => prev.filter(u => u !== username));
      toast({ title: "User unblocked", description: `${username} has been unblocked successfully.` });
    }
  };

  const reportUser = async (
    username: string,
    reason: string,
    context?: { type?: "chat" | "video" | "profile"; conversationId?: string | null; channel?: string | null }
  ) => {
    if (!user) return;
    const { data: target } = await (supabase.from("public_profiles" as any) as any).select("user_id").eq("username", username).single();
    if (target) {
      const result = await submitReport({
        reportedUserId: target.user_id,
        reason,
        reportType: context?.type ?? "profile",
        conversationId: context?.conversationId ?? undefined,
        sessionChannel: context?.channel ?? undefined,
      });
      if (!result.ok) {
        console.warn("[reportUser] submitReport failed", result.error);
        toast({ title: "Report failed", description: result.error || "Please try again.", variant: "destructive" });
        return;
      }
      setReportedUsers(prev => [...prev, username]);
      toast({ title: "Report submitted", description: "Thank you for helping keep the community safe." });
    }
  };

  const getOrCreateConversation = async (otherUserId: string): Promise<string> => {
    if (!user) throw new Error("Not authenticated");
    
    // Use server-side function to handle both participants atomically
    const { data, error } = await supabase.rpc("create_conversation_with_participant" as any, {
      p_other_user_id: otherUserId,
    });
    
    if (error || !data) {
      console.error("create_conversation_with_participant error:", error);
      throw new Error("Failed to create conversation");
    }
    
    // Reload conversations to pick up the new one
    await loadConversations(user.id);
    
    return data as string;
  };

  const sendConversationMessage = async (conversationId: string, text: string): Promise<string> => {
    if (!user) {
      toast({ title: "Not signed in", description: "Please log in again.", variant: "destructive" });
      throw new Error("Not authenticated");
    }
    // Optimistic UI: insert a temp message immediately so the sender always sees it.
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const optimistic: Message = {
      id: tempId,
      text,
      sender: "me",
      timestamp: new Date(),
    };
    setConversationMessages((prev) => ({
      ...prev,
      [conversationId]: [
        ...(prev[conversationId] || []).filter((m) => !(m.failed && m.sender === "me" && m.text === text)),
        optimistic,
      ],
    }));

    try {
      const { data, error } = await supabase
        .from("messages")
        .insert({ conversation_id: conversationId, sender_id: user.id, text, content: text } as any)
        .select("id, created_at")
        .single();
      if (error) throw error;
      // Reconcile temp id with real id so realtime dedupe works.
      setConversationMessages((prev) => {
        const list = prev[conversationId] || [];
        return {
          ...prev,
          [conversationId]: list.map((m) =>
            m.id === tempId ? { ...m, id: (data as any).id, timestamp: new Date((data as any).created_at) } : m
          ),
        };
      });
      // Bump conversation preview locally too
      setConversations((prev) =>
        prev.map((c) =>
          c.id === conversationId ? { ...c, lastMessage: text, lastMessageTime: new Date() } : c
        )
      );
      return (data as any).id as string;
    } catch (error: any) {
      console.log("Send message error:", error);
      // Keep the optimistic message visible and mark it as failed so the UI can retry.
      setConversationMessages((prev) => ({
        ...prev,
        [conversationId]: (prev[conversationId] || []).map((m) =>
          m.id === tempId ? { ...m, failed: true } : m
        ),
      }));
      toast({
        title: "Couldn't send",
        description: error?.message || "Network error. Please try again.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const sendFriendRequest = async (username: string) => {
    if (!user) return;
    if (user.isAnonymous) {
      const err: any = new Error("GUEST_BLOCKED");
      err.code = "GUEST_BLOCKED";
      throw err;
    }

    // Friend requests are FREE. Use SECURITY DEFINER RPC so we can find users by username
    // even when RLS on profiles only exposes the caller's own row.
    const { data: targetRows } = await (supabase.rpc as any)("find_user_by_username", { p_username: username });
    const target = Array.isArray(targetRows) ? targetRows[0] : targetRows;
    if (!target?.user_id) { toast({ title: "Error", description: "User not found", variant: "destructive" }); return; }
    if (target.user_id === user.id) { toast({ title: "That's you", description: "You can't add yourself", variant: "destructive" }); return; }
    
    const { error } = await supabase.from("friend_requests").insert({
      from_user_id: user.id,
      to_user_id: target.user_id,
    });
    
    if (error) {
      if (error.code === "23505") toast({ title: "Already sent", description: "Friend request already sent" });
      else toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    
    setSentRequests(prev => [...prev, username]);
    toast({ title: "Request sent!" });
  };

  const acceptFriendRequest = async (id: string) => {
    const { error } = await supabase.rpc("accept_friend_request", { request_id: id });
    if (error) {
      toast({ title: "Error", description: "Failed to accept request", variant: "destructive" });
      return;
    }
    if (user) {
      await loadFriendRequests(user.id);
      await loadFriends(user.id);
    }
    // Reward Aura for making a new friend (both sides; this side is the accepter)
    void addAura(10, "friend_added");
    toast({ title: "Friend added! 🎉" });
  };

  const ignoreFriendRequest = async (id: string) => {
    await supabase.from("friend_requests").update({ status: "ignored" }).eq("id", id);
    setFriendRequests(prev => prev.filter(r => r.id !== id));
  };

  const unfriend = async (username: string) => {
    if (!user) return;
    const friend = friends.find(f => f.username === username);
    if (!friend) return;

    // Server-side cleanup: delete friendship, pending requests, shared conversation + messages.
    const { error } = await supabase.rpc("unfriend_and_cleanup" as any, { p_other_user_id: friend.friendUserId });
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }

    setFriends(prev => prev.filter(f => f.username !== username));
    setConversations(prev => prev.filter(c => c.otherUserId !== friend.friendUserId));
    setSentRequests(prev => prev.filter(u => u !== username));
    toast({ title: "Unfriended", description: `${username} removed from friends` });
  };

  const searchUsers = async (query: string) => {
    if (!user) return [];
    const { data } = await (supabase.rpc as any)("search_users_by_username", { p_query: query });
    return (data as any[]) || [];
  };

  const refreshBalance = async () => {
    if (!user) return;
    const { data } = await supabase.from("profiles").select("coins, diamonds").eq("user_id", user.id).single();
    if (data) {
      setUser(prev => prev ? { ...prev, coins: data.coins, diamonds: (data as any).diamonds ?? prev.diamonds } : null);
    }
  };

  const acceptTerms = async () => {
    if (!user) return;
    await supabase.rpc("accept_terms");
    setHasAcceptedTerms(true);
  };

  const addAura = async (amount: number, reason?: string) => {
    if (!user || !amount) return;
    try {
      const { data } = await (supabase.rpc as any)("add_aura_self", { p_amount: amount, p_reason: reason ?? null });
      if (typeof data === "number") setUser(prev => prev ? { ...prev, auraScore: data } : null);
    } catch (e) { console.warn("addAura failed", e); }
  };

  const markConversationRead = useCallback(async (conversationId: string) => {
    if (!conversationId) return;
    // Optimistic UI: clear unread immediately
    setConversations(prev => prev.map(c => c.id === conversationId ? { ...c, unread: 0 } : c));
    try {
      await supabase.rpc("mark_conversation_read", { p_conversation_id: conversationId });
    } catch (err) {
      console.error("[markConversationRead] failed:", err);
    }
  }, []);

  const setActiveConversationWrapped = useCallback((id: string | null) => {
    setActiveConversation(id);
    if (id) void markConversationRead(id);
  }, [markConversationRead]);

  return (
    <AppContext.Provider
      value={{
        user, isAuthenticated, banInfo, hasSeenWelcome, hasOnboarded, hasSurveyed, hasAcceptedTerms, darkMode, toggleDarkMode,
        messages, conversations, activeConversation, conversationMessages,
        blockedUsers, reportedUsers, matchFilters, friendRequests, friends, sentRequests, loading,
        login, signup, loginAsGuest, upgradeAccount, logout, completeWelcome, completeOnboarding, completeSurvey,
        updateUser, toggleAvailability, addMessage, clearMessages,
        spendCoins, spendDiamonds,
        setActiveConversation: setActiveConversationWrapped, sendConversationMessage, markConversationRead,
        blockUser, unblockUser, reportUser, setMatchFilters, sendFriendRequest,
        acceptFriendRequest, ignoreFriendRequest, unfriend, searchUsers, getOrCreateConversation,
        refreshBalance, acceptTerms, addAura,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
};
